import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { employeeApi } from '../../api/employeeApi';
import { Employee, EmployeeProfile, CreateEmployeeQuick } from '../../types';

interface EmployeeState {
  employees: Employee[];
  totalCount: number;
  selectedEmployee: EmployeeProfile | null;
  loading: boolean;
  error: string | null;
}

const initialState: EmployeeState = {
  employees: [],
  totalCount: 0,
  selectedEmployee: null,
  loading: false,
  error: null,
};

export const fetchEmployees = createAsyncThunk(
  'employees/fetchAll',
  async ({ page, pageSize, searchTerm, sortBy, sortDirection, filters }: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    filters?: {
      departmentId?: string;
      designationId?: string;
      branchId?: string;
      employmentStatus?: number;
      joinedDateFrom?: string;
      joinedDateTo?: string;
    };
  } = {}, { rejectWithValue }) => {
    try {
      const [employees, totalCount] = await Promise.all([
        employeeApi.getAll(page, pageSize, searchTerm, filters, sortBy, sortDirection),
        employeeApi.getCount(searchTerm, filters),
      ]);
      return { employees, totalCount };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch employees');
    }
  }
);

export const fetchEmployeeProfile = createAsyncThunk(
  'employees/fetchProfile',
  async (id: string, { rejectWithValue }) => {
    try {
      return await employeeApi.getProfile(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch employee profile');
    }
  }
);

export const createEmployeeQuick = createAsyncThunk(
  'employees/createQuick',
  async (employee: CreateEmployeeQuick, { rejectWithValue }) => {
    try {
      return await employeeApi.createQuick(employee);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create employee');
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  'employees/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await employeeApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete employee');
    }
  }
);

const employeeSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    clearSelectedEmployee: (state) => {
      state.selectedEmployee = null;
    },
    clearEmployeeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action: PayloadAction<{ employees: Employee[]; totalCount: number }>) => {
        state.loading = false;
        state.employees = action.payload.employees;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch profile
      .addCase(fetchEmployeeProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmployeeProfile.fulfilled, (state, action: PayloadAction<EmployeeProfile>) => {
        state.loading = false;
        state.selectedEmployee = action.payload;
      })
      .addCase(fetchEmployeeProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create quick
      .addCase(createEmployeeQuick.pending, (state) => {
        state.loading = true;
      })
      .addCase(createEmployeeQuick.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createEmployeeQuick.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteEmployee.fulfilled, (state, action: PayloadAction<string>) => {
        state.employees = state.employees.filter(e => e.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });
  },
});

export const { clearSelectedEmployee, clearEmployeeError } = employeeSlice.actions;
export default employeeSlice.reducer;
